import React, { useState, useEffect } from 'react';
import { BackupService, ImportResult } from '../services/backupService';
import { isMobile, getExportButtonText, getImportButtonText, getFileOperationTips } from '../utils/mobileDetection';
import { useStartOverModal } from '../context/StartOverModalContext';
import { useStepWorkspace } from '../context/StepWorkspaceContext';
import styles from '../styles/backupRestoreModal.module.css';

interface BackupRestoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ProgramTrack {
  id: string;
  label: string;
  program: string;
  track: string;
}

// Backup Modal Component
const BackupModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  programTrack: ProgramTrack;
}> = ({ isOpen, onClose, programTrack }) => {
  const { notifyStepWorkspaceChanged } = useStepWorkspace();
  const [filename, setFilename] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clearAfterBackup, setClearAfterBackup] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [dataWasCleared, setDataWasCleared] = useState(false);
  const [showNoDataModal, setShowNoDataModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const defaultFilename = BackupService.generateDefaultFilename(
        programTrack.program, 
        programTrack.track
      );
      setFilename(defaultFilename);
      setError(null);
      setClearAfterBackup(false);
    }
  }, [isOpen, programTrack]);

  const handleBackup = async () => {
    if (!filename.trim()) {
      setError('Please enter a filename');
      return;
    }

    // Check if there's any data to backup
    try {
      const hasData = await BackupService.hasCurrentData(programTrack.program, programTrack.track);
      if (!hasData) {
        setShowNoDataModal(true);
        return;
      }
    } catch (error) {
      setError('Unable to check current data. Please try again.');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const result = await BackupService.exportProgress(
        programTrack.program, 
        programTrack.track, 
        filename
      );
      
      if (result.success) {
        let message = 'Backup completed';
        
        // Clear data after backup if checkbox is checked
        if (clearAfterBackup) {
          try {
            const clearResult = await BackupService.clearProgress(
              programTrack.program, 
              programTrack.track
            );
            
            if (clearResult.success) {
              message = 'Backup completed and data in application cleared';
              setDataWasCleared(true);
            } else {
              message = `Backup completed, but failed to clear data: ${clearResult.error}`;
              setDataWasCleared(false);
            }
          } catch (clearError) {
            message = `Backup completed, but failed to clear data: ${clearError}`;
            setDataWasCleared(false);
          }
        } else {
          setDataWasCleared(false);
        }
        
        setSuccessMessage(message);
        setShowSuccess(true);
      } else {
        setError(result.error || 'Backup failed');
      }
    } catch (error) {
      setError('Backup failed: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessOk = () => {
    if (dataWasCleared) {
      notifyStepWorkspaceChanged();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>📤 Backup Progress</h3>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>
          
          {showSuccess ? (
            <div className={styles.section}>
              <div className={styles.successMessage}>
                ✅ {successMessage}
              </div>
              <div className={styles.actionControls}>
                <button
                  onClick={handleSuccessOk}
                  className={styles.primaryButton}
                >
                  OK
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.section}>
                <p>This will backup your progress for {programTrack.label}, including completed steps, inventory items, and personal notes.</p>
                
                <div className={styles.inputGroup}>
                  <label htmlFor="filename">Filename:</label>
                  <input
                    id="filename"
                    type="text"
                    value={filename}
                    onChange={(e) => setFilename(e.target.value)}
                    placeholder="Enter filename"
                    className={styles.filenameInput}
                  />
                </div>
                
                {/* Laptop backup experience */}
                {!isMobile() && (
                  <div className={styles.laptopInfo}>
                    <p><strong>📁 Location Selection:</strong> You will be prompted to select a folder where you want to store the backup.</p>
                    <p><strong>🔐 Encryption:</strong> The backup will be encrypted and can only be read by this application for future restore operations.</p>
                  </div>
                )}
                
                {/* Clear after backup checkbox */}
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={clearAfterBackup}
                      onChange={(e) => setClearAfterBackup(e.target.checked)}
                      className={styles.checkbox}
                    />
                    Clear data after backup
                  </label>
                </div>
                
                <div className={styles.actionControls}>
                  <button
                    onClick={handleBackup}
                    disabled={isLoading || !filename.trim()}
                    className={styles.primaryButton}
                  >
                    {isLoading ? 'Processing...' : getExportButtonText()}
                  </button>
                  <button
                    onClick={onClose}
                    className={styles.secondaryButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {error && (
                <div className={styles.errorMessage}>
                  ❌ {error}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* No Data Modal */}
      {showNoDataModal && (
        <div className={styles.modalOverlay} onClick={() => setShowNoDataModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>ℹ️ No Data to Backup</h3>
              <button className={styles.closeButton} onClick={() => setShowNoDataModal(false)}>×</button>
            </div>
            <div className={styles.section}>
              <p>There is no data to backup. Please complete some steps or add inventory items first.</p>
              <div className={styles.actionControls}>
                <button
                  onClick={() => setShowNoDataModal(false)}
                  className={styles.primaryButton}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Restore Modal Component
const RestoreModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  programTrack: ProgramTrack;
  hasCurrentData: boolean;
}> = ({ isOpen, onClose, programTrack, hasCurrentData }) => {
  const { notifyStepWorkspaceChanged } = useStepWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showRestorePreview, setShowRestorePreview] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccess(null);
      setOverwriteExisting(false);
      setShowRestorePreview(false);
      setImportResult(null);
    }
  }, [isOpen]);

  const handleRestore = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await BackupService.importProgress();
      
      if (result.success) {
        setImportResult(result);
        setShowRestorePreview(true);
      } else {
        setError(result.error || 'Restore failed');
      }
    } catch (error) {
      setError('Restore failed: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyRestore = async () => {
    if (!importResult || !importResult.data) return;
    
    // Check if user has current data and overwrite is not checked
    if (hasCurrentData && !overwriteExisting) {
      setError('Please check "Overwrite existing progress" to continue, or clear your current data first');
      setShowRestorePreview(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await BackupService.applyImportedData(importResult.data as any, programTrack);
      
      if (result.success) {
        // Show detailed restore results
        if (result.results) {
          const { successful, warnings, skipped } = result.results;
          let message = `Restore completed successfully!\n\n`;
          message += `✅ Successfully restored: ${successful} items\n`;
          if (warnings > 0) {
            message += `⚠️ Restored with warnings: ${warnings} items\n`;
          }
          if (skipped > 0) {
            message += `❌ Skipped: ${skipped} items (could not be matched)\n`;
          }
          setSuccess(message);
        } else {
          setSuccess('Restore completed successfully');
        }
        setShowRestorePreview(false);
        notifyStepWorkspaceChanged();
      } else {
        setError(result.error || 'Restore failed');
      }
    } catch (error) {
      setError('Restore failed: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearAndRestore = async () => {
    if (!importResult || !importResult.data) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First clear current data
      const clearResult = await BackupService.clearProgress(
        programTrack.program, 
        programTrack.track
      );
      
      if (!clearResult.success) {
        setError(clearResult.error || 'Failed to clear current data');
        return;
      }
      
      // Then restore new data
      const importResult2 = await BackupService.applyImportedData(importResult.data, programTrack);
      
      if (importResult2.success) {
        // Show detailed restore results
        if (importResult2.results) {
          const { successful, warnings, skipped } = importResult2.results;
          let message = `Data cleared and restore completed successfully!\n\n`;
          message += `✅ Successfully restored: ${successful} items\n`;
          if (warnings > 0) {
            message += `⚠️ Restored with warnings: ${warnings} items\n`;
          }
          if (skipped > 0) {
            message += `❌ Skipped: ${skipped} items (could not be matched)\n`;
          }
          setSuccess(message);
        } else {
          setSuccess('Data cleared and restored successfully');
        }
        setShowRestorePreview(false);
        notifyStepWorkspaceChanged();
      } else {
        setError(importResult2.error || 'Restore failed');
      }
    } catch (error) {
      setError('Clear and restore failed: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatBackupDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStepsWithData = () => {
    if (!importResult?.data?.progress) return [];
    
    const stepsWithData = [];
    for (const [stepKey, stepData] of Object.entries(importResult.data.progress)) {
      const stepNumber = parseInt(stepKey.replace('step', ''));
      if (!isNaN(stepNumber)) {
        // Only include steps that have meaningful data (not "Not Started")
        const hasCompletedActions = stepData.data?.actions?.some((action: any) => action.completed);
        const isCompleted = stepData.completed;
        
        if (hasCompletedActions || isCompleted) {
          stepsWithData.push(stepNumber);
        }
      }
    }
    return stepsWithData.sort((a, b) => a - b);
  };

  const getInventorySummary = () => {
    if (!importResult?.data?.inventories) return { totalEntries: 0, totalInventories: 0 };
    
    const { resentments, fears, harms } = importResult.data.inventories;
    const totalEntries = (resentments?.length || 0) + (fears?.length || 0) + (harms?.length || 0);
    const totalInventories = [resentments, fears, harms].filter(arr => arr && arr.length > 0).length;
    
    return { totalEntries, totalInventories };
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>📥 Restore Progress</h3>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>
          
          <div className={styles.section}>
            <p>This will load progress data for {programTrack.label} from a backup file.</p>
            
            {hasCurrentData && (
              <div className={styles.warning}>
                ⚠️ You have current progress and content for {programTrack.label}. 
                If you don't want this import to overwrite it, choose cancel and use backup.
              </div>
            )}
            
            <div className={styles.actionControls}>
              <button
                onClick={handleRestore}
                disabled={isLoading}
                className={styles.primaryButton}
              >
                {isLoading ? 'Processing...' : getImportButtonText()}
              </button>
              <button
                onClick={onClose}
                className={styles.secondaryButton}
              >
                Cancel
              </button>
            </div>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              ❌ {error}
            </div>
          )}

          {success && (
            <div className={styles.successMessage}>
              ✅ {success}
            </div>
          )}
        </div>
      </div>

      {/* Restore Preview Modal */}
      {showRestorePreview && importResult && (
        <div className={styles.modalOverlay} onClick={() => setShowRestorePreview(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Restore Preview</h3>
              <button className={styles.closeButton} onClick={() => setShowRestorePreview(false)}>×</button>
            </div>
            
            <div className={styles.restorePreview}>
              <h4>File Contents:</h4>
              <div className={styles.previewContent}>
                <p><strong>Program:</strong> {programTrack?.program || 'Unknown'}</p>
                <p><strong>Track:</strong> {programTrack?.track || 'Unknown'}</p>
                <p><strong>Backup Date:</strong> {importResult.summary?.exportDate ? formatBackupDate(importResult.summary.exportDate) : 'Unknown'}</p>
                <p><strong>Steps with Data:</strong> {getStepsWithData().length > 0 ? getStepsWithData().join(', ') : 'None'}</p>
                <p><strong>Inventories:</strong> {(() => {
                  const { totalEntries, totalInventories } = getInventorySummary();
                  if (totalEntries === 0) return 'None found';
                  if (totalInventories === 1) return `${totalEntries} entries`;
                  return `${totalEntries} entries across ${totalInventories} inventories`;
                })()}</p>
              </div>
             
              {/* Overwrite checkbox if there's existing data */}
              {hasCurrentData && (
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={overwriteExisting}
                      onChange={(e) => setOverwriteExisting(e.target.checked)}
                      className={styles.checkbox}
                    />
                    Overwrite existing progress
                  </label>
                </div>
              )}
             
              {hasCurrentData ? (
                <div className={styles.restoreActions}>
                  <button
                    onClick={handleClearAndRestore}
                    disabled={isLoading}
                    className={styles.clearAndRestoreButton}
                  >
                    {isLoading ? 'Processing...' : 'Clear & Restore'}
                  </button>
                  <button
                    onClick={() => setShowRestorePreview(false)}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className={styles.restoreActions}>
                  <button
                    onClick={handleApplyRestore}
                    disabled={isLoading}
                    className={styles.applyRestoreButton}
                  >
                    {isLoading ? 'Restoring...' : 'Restore Data'}
                  </button>
                  <button
                    onClick={() => setShowRestorePreview(false)}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Clear Modal Component
const ClearModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  programTrack: ProgramTrack;
}> = ({ isOpen, onClose, programTrack }) => {
  const { notifyStepWorkspaceChanged } = useStepWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [clearConfirmText, setClearConfirmText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccess(null);
      setClearConfirmText('');
    }
  }, [isOpen]);

  const handleClear = async () => {
    if (clearConfirmText.trim() === '') {
      setError('Please type "clear" to confirm');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await BackupService.clearProgress(
        programTrack.program, 
        programTrack.track
      );
      
      if (result.success) {
        setSuccess('Data cleared successfully');
        notifyStepWorkspaceChanged();
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result.error || 'Clear failed');
      }
    } catch (error) {
      setError('Clear failed: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>🗑️ Clear Progress</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div className={styles.section}>
          <p>This will permanently delete all your progress data for {programTrack.label}. This action cannot be undone.</p>
          
          <div className={styles.warning}>
            ⚠️ You have current data for {programTrack.label}. 
            Make sure you have backed up your data first.
          </div>
          
          <div className={styles.inputGroup}>
            <label htmlFor="clearConfirm">Type "clear" to confirm:</label>
            <input
              id="clearConfirm"
              type="text"
              value={clearConfirmText}
              onChange={(e) => setClearConfirmText(e.target.value)}
              placeholder=""
              className={styles.confirmInput}
            />
          </div>
          
          <div className={styles.actionControls}>
            <button
              onClick={handleClear}
              disabled={isLoading || clearConfirmText.toLowerCase() !== 'clear'}
              className={styles.confirmClearButton}
            >
              {isLoading ? 'Clearing...' : 'Clear All Data'}
            </button>
            <button
              onClick={onClose}
              className={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            ❌ {error}
          </div>
        )}

        {success && (
          <div className={styles.successMessage}>
            ✅ {success}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Modal Component
const BackupRestoreModal: React.FC<BackupRestoreModalProps> = ({ isOpen, onClose }) => {
  const { openStartOverModal } = useStartOverModal();
  const [selectedProgramTrack, setSelectedProgramTrack] = useState<ProgramTrack | null>(null);
  const [hasCurrentData, setHasCurrentData] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);

  // Available program-tracks (for now just SAA 12-steps)
  const availableProgramTracks: ProgramTrack[] = [
    {
      id: 'SAA-12-steps',
      label: 'SAA - 12 Steps',
      program: 'SAA',
      track: '12-steps'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      // Auto-select the first program-track
      setSelectedProgramTrack(availableProgramTracks[0]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedProgramTrack) {
      // Check if user has current data for this program-track
      checkCurrentData();
    }
  }, [selectedProgramTrack]);

  const checkCurrentData = async () => {
    if (!selectedProgramTrack) return;
    
    try {
      const hasData = await BackupService.hasCurrentData(
        selectedProgramTrack.program, 
        selectedProgramTrack.track
      );
      setHasCurrentData(hasData);
    } catch (error) {
      console.error('Error checking current data:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Modal */}
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Backup & Restore</h2>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>

          <div className={styles.eolBanner}>
            <p>
              <strong>Backup &amp; Restore is being replaced.</strong> To save your step work and start over, use{' '}
              <strong>Save &amp; Start over</strong> in the menu — we keep a copy on the server for you.
            </p>
            <button
              type="button"
              className={styles.eolBannerButton}
              onClick={() => {
                onClose();
                openStartOverModal();
              }}
            >
              Open Save &amp; Start over
            </button>
          </div>

          {/* Program-Track Selection */}
          <div className={styles.section}>
            <h3>Select Program & Track</h3>
            <div className={styles.programTrackSelector}>
              <select
                value={selectedProgramTrack?.id || ''}
                onChange={(e) => {
                  const selected = availableProgramTracks.find(pt => pt.id === e.target.value);
                  setSelectedProgramTrack(selected || null);
                }}
                className={styles.programTrackSelect}
              >
                {availableProgramTracks.map(pt => (
                  <option key={pt.id} value={pt.id}>
                    {pt.label}
                  </option>
                ))}
              </select>
              <p className={styles.moreComingSoon}>More programs coming soon</p>
            </div>
          </div>

          {/* Action Selection */}
          {selectedProgramTrack && (
            <div className={styles.section}>
              <h3>Choose Action</h3>
              <div className={styles.actionButtons}>
                <button
                  onClick={() => setShowBackupModal(true)}
                  className={styles.actionButton}
                >
                  📤 Backup Progress
                </button>
                <button
                  onClick={() => setShowRestoreModal(true)}
                  className={styles.actionButton}
                >
                  📥 Restore Progress
                </button>
                <button
                  onClick={() => setShowClearModal(true)}
                  className={styles.actionButton}
                  disabled={!hasCurrentData}
                >
                  🗑️ Clear Progress
                </button>
              </div>
            </div>
          )}

          {/* Mobile Tips */}
          {isMobile() && (
            <div className={styles.mobileTips}>
              <h3>📱 Mobile Tips</h3>
              <ul>
                {getFileOperationTips().map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Child Modals */}
      {selectedProgramTrack && (
        <>
          <BackupModal
            isOpen={showBackupModal}
            onClose={() => setShowBackupModal(false)}
            programTrack={selectedProgramTrack}
          />
          
          <RestoreModal
            isOpen={showRestoreModal}
            onClose={() => setShowRestoreModal(false)}
            programTrack={selectedProgramTrack}
            hasCurrentData={hasCurrentData}
          />
          
          <ClearModal
            isOpen={showClearModal}
            onClose={() => setShowClearModal(false)}
            programTrack={selectedProgramTrack}
          />
        </>
      )}
    </>
  );
};

export default BackupRestoreModal; 